/**
 * Simple Test Component για React Query
 * Άνοιξε αυτό το component για να δεις πώς λειτουργεί το React Query
 */

import React from 'react';
import { Box, Card, CardContent, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { useEmployees } from './hooks/useEmployees';
import { useQuery } from '@tanstack/react-query';
import { positionsApi } from './lib/api';

function TestReactQuery() {
  // Test 1: Employees με React Query
  const { 
    data: employees = [], 
    isLoading: employeesLoading, 
    error: employeesError,
    refetch: refetchEmployees 
  } = useEmployees(true);

  // Test 2: Positions με React Query
  const { 
    data: positions = [], 
    isLoading: positionsLoading,
    error: positionsError 
  } = useQuery({
    queryKey: ['positions'],
    queryFn: positionsApi.getAll,
  });

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600, color: 'primary.main' }}>
        🧪 React Query Test Page
      </Typography>

      {/* Test 1: Employees */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Test 1: Employees (με React Query)
          </Typography>
          
          {employeesLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography>Φόρτωση υπαλλήλων...</Typography>
            </Box>
          )}
          
          {employeesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Σφάλμα: {employeesError.message}
            </Alert>
          )}
          
          {!employeesLoading && !employeesError && (
            <>
              <Typography sx={{ mb: 2, color: 'success.main' }}>
                ✅ Βρέθηκαν {employees.length} υπάλληλοι!
              </Typography>
              <Box sx={{ mb: 2 }}>
                {employees.slice(0, 5).map(emp => (
                  <Typography key={emp.id} sx={{ mb: 1 }}>
                    • {emp.name} - {emp.position} (€{emp.hourly_wage}/ώρα)
                  </Typography>
                ))}
                {employees.length > 5 && (
                  <Typography color="textSecondary">
                    ... και {employees.length - 5} ακόμα
                  </Typography>
                )}
              </Box>
              <Button 
                variant="outlined" 
                onClick={() => refetchEmployees()}
                sx={{ mt: 1 }}
              >
                🔄 Refresh Data
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Test 2: Positions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Test 2: Positions (με React Query)
          </Typography>
          
          {positionsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography>Φόρτωση θέσεων...</Typography>
            </Box>
          )}
          
          {positionsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Σφάλμα: {positionsError.message}
            </Alert>
          )}
          
          {!positionsLoading && !positionsError && (
            <>
              <Typography sx={{ mb: 2, color: 'success.main' }}>
                ✅ Βρέθηκαν {positions.length} θέσεις!
              </Typography>
              <Box>
                {positions.map(pos => (
                  <Typography key={pos.id} sx={{ mb: 1 }}>
                    • {pos.name}
                  </Typography>
                ))}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card sx={{ backgroundColor: '#f0f7ff' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            💡 Τι βλέπεις εδώ;
          </Typography>
          <Typography sx={{ mb: 1 }}>
            • <strong>Loading states</strong> - Αυτόματα loading indicators
          </Typography>
          <Typography sx={{ mb: 1 }}>
            • <strong>Error handling</strong> - Αυτόματα error messages
          </Typography>
          <Typography sx={{ mb: 1 }}>
            • <strong>Caching</strong> - Τα data cache-άρονται (δεν κάνει fetch κάθε φορά)
          </Typography>
          <Typography sx={{ mb: 1 }}>
            • <strong>Refetch</strong> - Μπορείς να refresh τα data με ένα button
          </Typography>
          <Typography sx={{ mb: 2 }}>
            • <strong>Less code</strong> - Μόνο 1 γραμμή αντί για 10+!
          </Typography>
          <Alert severity="info">
            Αν βλέπεις data εδώ, το React Query λειτουργεί! 🎉
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
}

export default TestReactQuery;

